<template>
  <div class="chat-list-page">
    <div class="page-header">
      <h1>聊天</h1>
      <p class="sub">选择商家客服，咨询商品与订单问题（官方客服请到「我的 - 我的服务」）</p>
    </div>

    <div class="section-title">商家客服</div>

    <van-loading v-if="loading" class="loading" color="#ff6b35" />

    <template v-else>
      <div v-if="shopList.length === 0" class="empty">
        <van-icon name="shop-o" size="48" color="#ccc" />
        <p>还没有和商家聊过</p>
        <p class="empty-tip">可在商品详情页或订单里点击「客服」开始咨询</p>
      </div>

      <div
        v-for="shop in shopList"
        :key="shop.merchant_id"
        class="shop-item"
        @click="openChat(shop)"
      >
        <!-- 头像：点击进入店铺主页（商家资质需安全验证后查看） -->
        <div class="shop-logo" title="查看店铺" @click.stop="openShop(shop)">
          <img v-if="shop.shop_logo" :src="shop.shop_logo" alt="">
          <span v-else>🏪</span>
        </div>

        <!-- 右侧两行：第一行 店铺名字 + 消息时间；第二行 消息预览 -->
        <div class="shop-main">
          <div class="row-top">
            <span class="shop-name">
              {{ shop.shop_name }}
              <span v-if="shop.is_official === 1" class="official-tag">自营</span>
            </span>
            <span class="msg-time">{{ formatTime(shop.updated_at) }}</span>
          </div>
          <div class="row-bottom">
            <span class="msg-preview">{{ shop.last_message || '（暂无消息）' }}</span>
            <span v-if="shop.unread_count > 0" class="badge">{{ shop.unread_count > 99 ? '99+' : shop.unread_count }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- 底部导航占位 -->
    <div class="bottom-placeholder"></div>
  </div>
</template>

<script>
import { getChatMerchantsApi } from '@/api/chat'
import chatSocket from '@/utils/ws'
import { Toast } from 'vant'

// 轮询间隔（毫秒）：WebSocket 不可用时的兜底；已连接时低频校验
const POLL_INTERVAL_HAS_WS = 15000
const POLL_INTERVAL_NO_WS = 6000

export default {
  name: 'ChatList',
  data () {
    return {
      loading: false,
      shopList: [],
      pollTimer: null,
      // WebSocket 收到新消息后的聚合刷新定时器（避免频繁请求）
      refreshTimer: null
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    }
  },
  created () {
    this.loadShops()
    this.bindSocket()
  },
  mounted () {
    // 从聊天页返回 / 切回前台时立即刷新一次，保证消息预览是最新的
    this._onVisible = () => {
      if (document.visibilityState === 'visible') {
        this.loadShops(true)
      }
    }
    document.addEventListener('visibilitychange', this._onVisible)
    this.startPolling()
  },
  beforeDestroy () {
    this.stopPolling()
    this.unbindSocket()
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
    if (this._onVisible) {
      document.removeEventListener('visibilitychange', this._onVisible)
      this._onVisible = null
    }
  },
  methods: {
    // ==================== 数据加载 ====================
    // silent = true 时后台静默刷新（不显示 loading，避免列表闪烁）
    async loadShops (silent = false) {
      if (!silent) {
        this.loading = true
      }
      try {
        const { data } = await getChatMerchantsApi()
        const list = (data && data.list) || []
        // 官方客服入口只在「我的 - 我的服务」展示，这里只列商家客服
        const next = list.filter(item => Number(item.merchant_id) > 0)
        // 数据无变化时不赋值，避免列表重新渲染造成闪烁
        if (JSON.stringify(next) !== JSON.stringify(this.shopList)) {
          this.shopList = next
        }
      } catch (e) {
        if (!silent) {
          this.shopList = []
        }
      } finally {
        this.loading = false
      }
    },

    // ==================== 实时刷新（消息预览不延迟） ====================
    bindSocket () {
      this.unbindSocket()
      // 买家 WebSocket 由 App.vue 建立连接，这里只监听事件
      this._onWsMessage = (payload) => {
        if (!payload || !payload.message) return
        // 先用推送的数据乐观更新预览与时间，界面立刻变化
        this.applyIncoming(payload)
        // 再聚合刷新一次（获取服务端准确的未读数等信息）
        this.scheduleRefresh()
      }
      this._onWsReady = () => {
        this.loadShops(true)
        this.startPolling()
      }
      chatSocket.on('message', this._onWsMessage)
      chatSocket.on('ready', this._onWsReady)
    },
    unbindSocket () {
      if (this._onWsMessage) chatSocket.off('message', this._onWsMessage)
      if (this._onWsReady) chatSocket.off('ready', this._onWsReady)
      this._onWsMessage = null
      this._onWsReady = null
    },
    // 用 WebSocket 推送的消息即时更新对应店铺的预览 / 时间 / 未读
    applyIncoming (payload) {
      const message = payload.message || {}
      const session = payload.session || {}
      // 会话渠道 id（官方自营店铺沿用官方客服渠道 0，因此按 chat_merchant_id / merchant_id 匹配）
      const channelId = session.merchant_id !== undefined ? Number(session.merchant_id) : -1
      const shop = this.shopList.find(item => Number(item.chat_merchant_id) === channelId
        || Number(item.merchant_id) === channelId)
      if (!shop) {
        // 不在列表中的会话（例如商家客服首次主动联系）→ 直接刷新列表补上
        this.scheduleRefresh()
        return
      }
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      const preview = Number(message.msg_type) === 2 ? '[图片]' : String(message.content || '')
      shop.last_message = preview.length > 100 ? preview.slice(0, 100) + '…' : preview
      shop.updated_at = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} `
        + `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
      // 客服发来的消息未读 +1（买家自己发送的不增加未读）
      if (Number(message.sender_type) === 2) {
        shop.unread_count = (Number(shop.unread_count) || 0) + 1
      }
      // 有消息的会话排到最前
      const index = this.shopList.indexOf(shop)
      if (index > 0) {
        this.shopList.splice(index, 1)
        this.shopList.unshift(shop)
      }
    },
    scheduleRefresh () {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer)
      }
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null
        this.loadShops(true)
      }, 400)
    },
    // 兜底轮询：WebSocket 不可用时更频繁，可用时低频校验
    startPolling () {
      this.stopPolling()
      const interval = chatSocket.isOpen ? POLL_INTERVAL_HAS_WS : POLL_INTERVAL_NO_WS
      this.pollTimer = setInterval(() => {
        if (document.visibilityState === 'hidden') return
        this.loadShops(true)
      }, interval)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    // ==================== 展示 ====================
    // 消息时间：今天 HH:mm；昨天「昨天 HH:mm」；今年 MM-DD HH:mm；跨年显示完整日期
    formatTime (val) {
      if (!val) return ''
      const t = new Date(String(val).replace(' ', 'T'))
      if (isNaN(t.getTime())) return ''
      const pad = n => String(n).padStart(2, '0')
      const hm = pad(t.getHours()) + ':' + pad(t.getMinutes())
      const now = new Date()
      if (t.toDateString() === now.toDateString()) {
        return hm
      }
      const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
      if (t.toDateString() === yesterday.toDateString()) {
        return '昨天 ' + hm
      }
      if (t.getFullYear() === now.getFullYear()) {
        return pad(t.getMonth() + 1) + '-' + pad(t.getDate()) + ' ' + hm
      }
      return t.getFullYear() + '-' + pad(t.getMonth() + 1) + '-' + pad(t.getDate())
    },
    openChat (shop) {
      // chat_merchant_id：实际会话渠道（官方自营店铺沿用官方客服渠道，保证历史记录连续）
      const merchantId = shop && shop.chat_merchant_id !== undefined
        ? Number(shop.chat_merchant_id)
        : Number(shop && shop.merchant_id) || 0
      const shopName = (shop && shop.shop_name) || ''
      if (!this.isLogin) {
        const target = `/chat?merchantId=${merchantId}&shopName=${encodeURIComponent(shopName)}`
        this.$router.push(`/login?redirect=${encodeURIComponent(target)}`)
        return
      }
      this.$router.push({ path: '/chat', query: { merchantId: String(merchantId), shopName } })
    },
    // 点击店铺头像 → 进入店铺主页（店内可查看商家资质，需安全验证通过）
    openShop (shop) {
      const merchantId = Number(shop && shop.merchant_id) || 0
      if (merchantId <= 0) {
        Toast('该客服暂未关联店铺')
        return
      }
      this.$router.push('/shop/' + merchantId)
    }
  }
}
</script>

<style lang="less" scoped>
.chat-list-page {
  min-height: 100vh;
  background: #f5f6f8;
  padding-bottom: 60px;

  .page-header {
    padding: 20px 16px 12px;
    background: linear-gradient(135deg, #ff6b35, #ff8c42);
    color: #fff;

    h1 { font-size: 20px; margin: 0; }
    .sub { margin: 6px 0 0; font-size: 12px; opacity: 0.9; }
  }

  .section-title {
    padding: 6px 16px;
    font-size: 13px;
    color: #888;
  }

  .loading { padding: 30px 0; text-align: center; }

  .empty {
    padding: 60px 0;
    text-align: center;
    color: #999;
    font-size: 13px;

    .empty-tip {
      margin-top: 6px;
      font-size: 12px;
      color: #bbb;
    }
  }

  .shop-item {
    display: flex;
    align-items: center;
    background: #fff;
    margin: 0 12px 10px;
    padding: 12px 14px;
    border-radius: 12px;
    cursor: pointer;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

    .shop-logo {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      background: #fff5f0;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      margin-right: 12px;
      font-size: 22px;
      flex-shrink: 0;

      img { width: 100%; height: 100%; object-fit: cover; }
    }

    // 右侧两行：第一行 店铺名字 + 消息时间；第二行 消息预览 + 未读角标
    .shop-main {
      flex: 1;
      min-width: 0;

      .row-top {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .shop-name {
          font-size: 15px;
          color: #222;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          .official-tag {
            margin-left: 6px;
            font-size: 10px;
            font-weight: 400;
            color: #ff6b35;
            border: 1px solid #ffd2b0;
            border-radius: 4px;
            padding: 0 4px;
            line-height: 14px;
          }
        }

        .msg-time {
          flex-shrink: 0;
          margin-left: 8px;
          font-size: 12px;
          color: #b0b0b0;
        }
      }

      .row-bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 6px;

        .msg-preview {
          flex: 1;
          min-width: 0;
          font-size: 13px;
          color: #999;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .badge {
          flex-shrink: 0;
          min-width: 18px;
          height: 18px;
          line-height: 18px;
          text-align: center;
          border-radius: 9px;
          background: #ff3b30;
          color: #fff;
          font-size: 11px;
          padding: 0 5px;
          margin-left: 8px;
        }
      }
    }
  }

  .bottom-placeholder { height: 60px; }
}
</style>
